import type { LucideIcon } from 'lucide-react';
import { Check, ClipboardList, ShieldCheck, Truck, Video } from 'lucide-react';

import type { TrackingVerifyPayload } from '@/api/generated';

export type TrackingStatus = TrackingVerifyPayload['status'];

export type StatusStep = {
  key: string;
  label: string;
  completedLabel?: string;
  icon: LucideIcon;
};

export type BadgeVariant = 'upcoming' | 'action';

type StatusBadge = { text: string; variant: BadgeVariant };

export type ProgressBarVariant = 'default' | 'action';

type StatusConfig = {
  badge?: StatusBadge;
  title: string;
  description: (data: TrackingVerifyPayload) => React.ReactNode;
  currentStepKey: string | null;
  showVisitDetails: boolean;
  allStepsCompleted?: boolean;
  progressVariant?: ProgressBarVariant;
};

type StatusConfigEntry = StatusConfig & {
  resupply?: Partial<StatusConfig>;
};

export type StatusView = {
  badge?: StatusBadge;
  title: string;
  description: React.ReactNode;
  showVisitDetails: boolean;
  steps: StatusStep[];
  currentStepIndex: number;
  showProgressBar: boolean;
  progressVariant: ProgressBarVariant;
};

const SUBMITTED_STEP: StatusStep = { key: 'submitted', label: 'Submitted', icon: Check };
const CONFIRM_STEP: StatusStep = { key: 'confirm', label: 'Confirm', icon: ClipboardList };
const TELEVISIT_STEP: StatusStep = { key: 'televisit', label: 'Televisit', icon: Video };
const APPROVED_STEP: StatusStep = { key: 'approved', label: 'Approval', icon: ShieldCheck };
const SHIPPED_STEP: StatusStep = {
  key: 'shipped',
  label: 'Shipped',
  completedLabel: 'Delivered',
  icon: Truck,
};

const STATUS_CONFIGS: Record<TrackingStatus, StatusConfigEntry> = {
  submitted: {
    title: 'Your order has been submitted',
    description: () => "We've received your order and are getting started.",
    currentStepKey: 'submitted',
    showVisitDetails: false,
    resupply: { currentStepKey: 'confirm' },
  },
  upcoming_confirm: {
    badge: { text: 'Action Required', variant: 'action' },
    title: 'Please confirm your order',
    description: () => (
      <>
        We sent a text message to confirm your supplies this month.
        <span className="mt-2 block font-medium text-foreground">
          Please reply to confirm your order.
        </span>
      </>
    ),
    currentStepKey: 'confirm',
    showVisitDetails: false,
  },
  televisit_upcoming: {
    title: 'Your Televisit is coming up!',
    description: () =>
      'During the visit, a licensed provider will evaluate your medical needs for the requested supplies.',
    currentStepKey: 'televisit',
    showVisitDetails: true,
  },
  televisit_needs_reschedule: {
    badge: { text: 'Action Required', variant: 'action' },
    title: 'Please reschedule your visit',
    description: () =>
      'Looks like we missed you, please reschedule your Televisit in order to get covered supplies.',
    currentStepKey: 'televisit',
    showVisitDetails: true,
    progressVariant: 'action',
  },
  insurance_approval: {
    title: 'Awaiting insurance approval',
    description: () =>
      "We're reviewing clinical documents and submitting for insurance approval, this can sometimes take up to a week or two depending on your plan.",
    currentStepKey: 'approved',
    showVisitDetails: false,
  },
  shipped: {
    title: 'Your items are on the way!',
    description: (data) => {
      const shippedAt = data.order.shipping?.shipped_at;
      if (shippedAt) {
        return `Shipped on ${formatLongDate(shippedAt)}`;
      }
      return 'Your items have shipped and are on the way.';
    },
    currentStepKey: 'shipped',
    showVisitDetails: false,
  },
  delivered: {
    title: 'Delivery complete',
    description: (data) => {
      const deliveredAt = data.order.delivery?.delivered_at;
      if (deliveredAt) {
        return `Marked as delivered on ${formatLongDate(deliveredAt)}`;
      }
      return 'Your order has been delivered.';
    },
    currentStepKey: null,
    showVisitDetails: false,
    allStepsCompleted: true,
  },
  cancelled: {
    title: 'Order cancelled',
    description: (data) => {
      const cancellation = data.order.cancellation;
      const parts: string[] = [];
      if (cancellation?.cancelled_at) {
        parts.push(
          `Your order was marked cancelled on ${formatLongDate(cancellation.cancelled_at)}.`
        );
      }
      if (cancellation?.reason) {
        parts.push(`Reason: ${cancellation.reason}`);
      }
      return parts.length > 0 ? parts.join('\n\n') : 'This order has been cancelled.';
    },
    currentStepKey: null,
    showVisitDetails: false,
  },
  delayed: {
    badge: { text: 'Action Required', variant: 'action' },
    title: "Delayed\u2014we're having trouble reaching you",
    description: () =>
      "We've tried to contact you a few times. Please give us a call so we can complete your request!",
    currentStepKey: 'televisit',
    showVisitDetails: false,
    progressVariant: 'action',
    resupply: { currentStepKey: 'confirm' },
  },
  error: {
    title: "Sorry, we're having trouble getting your order info",
    description: () => 'Try again in a moment\u2014contact us if the issue continues.',
    currentStepKey: null,
    showVisitDetails: false,
  },
};

function formatLongDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function buildSteps(isResupply: boolean, includeTelevisit: boolean): StatusStep[] {
  return [
    isResupply ? CONFIRM_STEP : SUBMITTED_STEP,
    ...(includeTelevisit ? [TELEVISIT_STEP] : []),
    APPROVED_STEP,
    SHIPPED_STEP,
  ];
}

export function resolveStatusView(payload: TrackingVerifyPayload): StatusView {
  const isResupply = !!payload.is_resupply;
  const trackingStatus: TrackingStatus =
    payload.status in STATUS_CONFIGS ? (payload.status as TrackingStatus) : 'error';

  const { resupply, ...baseConfig } = STATUS_CONFIGS[trackingStatus];
  const config: StatusConfig = isResupply && resupply ? { ...baseConfig, ...resupply } : baseConfig;

  // Standard orders always include Televisit; resupplies only when the order has one on record.
  const includeTelevisit = !isResupply || !!payload.order.has_televisit;
  const steps = buildSteps(isResupply, includeTelevisit);

  let currentStepIndex: number;
  if (config.allStepsCompleted) {
    currentStepIndex = steps.length;
  } else if (config.currentStepKey) {
    currentStepIndex = steps.findIndex((step) => step.key === config.currentStepKey);
  } else {
    currentStepIndex = -1;
  }

  return {
    badge: config.badge,
    title: config.title,
    description: config.description(payload),
    showVisitDetails: config.showVisitDetails && payload.order.telehealth != null,
    steps,
    currentStepIndex,
    showProgressBar: currentStepIndex >= 0,
    progressVariant: config.progressVariant ?? 'default',
  };
}
